import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Shared script loading promise
let jitsiScriptPromise = null;

const loadJitsiScript = () => {
  if (jitsiScriptPromise) return jitsiScriptPromise;
  if (window.JitsiMeetExternalAPI) {
    jitsiScriptPromise = Promise.resolve();
    return jitsiScriptPromise;
  }
  const domain = process.env.REACT_APP_JITSI_DOMAIN || 'meet.jit.si';
  jitsiScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return jitsiScriptPromise;
};

const JitsiCall = ({ roomName, displayName, userEmail, onClose, userPhoto }) => {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!containerRef.current || !roomName) return;
    let cancelled = false;

    // Dispose any existing instance
    if (apiRef.current) {
      apiRef.current.dispose();
      apiRef.current = null;
    }

    loadJitsiScript().then(() => {
      if (cancelled || !containerRef.current) return;

      // Safely clear container before creating new instance
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }

      // Use custom Jitsi server if configured, fallback to public meet.jit.si
      const domain = process.env.REACT_APP_JITSI_DOMAIN || 'meet.jit.si';
      const options = {
        roomName: roomName,
        parentNode: containerRef.current,
        width: '100%',
        height: '100%',
        userInfo: {
          displayName: displayName || 'CultraMeet User',
          email: userEmail || '',
        },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinConfig: { enabled: false },
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          enableWelcomePage: false,
          enableClosePage: false,
          disableThirdPartyRequests: true,
          disableInviteFunctions: false,
          requireDisplayName: false,
          enableLobbyChat: false,
          toolbarButtons: [
            'microphone', 'camera', 'desktop', 'chat',
            'raisehand', 'participants-pane', 'tileview',
            'select-background', 'fullscreen', 'hangup', 'shareaudio'
          ],
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          SHOW_POWERED_BY: false,
          DEFAULT_BACKGROUND: '#1a1a2e',
          TOOLBAR_ALWAYS_VISIBLE: true,
          HIDE_INVITE_MORE_HEADER: false,
          MOBILE_APP_PROMO: false,
        },
      };

      const api = new window.JitsiMeetExternalAPI(domain, options);
      apiRef.current = api;

      if (userPhoto) {
        api.executeCommand('avatarUrl', userPhoto);
      }

      api.addEventListener('readyToClose', () => {
        onCloseRef.current();
      });

      api.addEventListener('videoConferenceLeft', () => {
        onCloseRef.current();
      });
    }).catch((err) => {
      console.error('Failed to load Jitsi:', err);
    });

    return () => {
      cancelled = true;
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, [roomName, displayName, userEmail, userPhoto]);

  return (
    <div className="fixed inset-0 z-[100] bg-black" data-testid="jitsi-call-view">
      <button
        data-testid="jitsi-close-btn"
        onClick={() => onCloseRef.current()}
        className="absolute top-4 right-4 z-[110] p-2 rounded-full bg-black/60 hover:bg-black/80 transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
};

export default JitsiCall;
